import StatefulSettings from "../controllers/StatefulSettings";
import {
  ITaskItem,
  ITaskBasketInfo,
  ISchedulerTimerInfo,
  ITaskTimerInfo,
  AuditMessage,
  TaskItem,
} from "../ts/Definitions";
import {
  AuditActionTypes,
  TraceLevel,
  PreselectType,
  AvailabilityDayType,
  WeekendHandling,
} from "../ts/enums";

/**
 * Manages task selection, scheduling, and enforcement logic including:
 * - Enforced task scheduling (daily, weekday, weekend)
 * - Random mandatory task selection
 * - Permanent task application and removal
 * - Schedule conflict checking
 */
export class TaskSelectionManager {
  private settings: StatefulSettings;
  private scheduleAddTaskToBasket: (item: TaskItem, reason: string) => void;
  private addPermanentTask: (task: TaskItem, taskSchedule: ITaskBasketInfo) => void;
  private handlePermanentTaskRemoval: (task: ITaskItem, taskSchedule: ITaskBasketInfo) => void;
  private getState: () => string;
  private setState: (state: string) => void;

  constructor(
    settings: StatefulSettings,
    scheduleAddTaskToBasket: (item: TaskItem, reason: string) => void,
    addPermanentTask: (task: TaskItem, taskSchedule: ITaskBasketInfo) => void,
    handlePermanentTaskRemoval: (task: ITaskItem, taskSchedule: ITaskBasketInfo) => void,
    getState: () => string,
    setState: (state: string) => void
  ) {
    this.settings = settings;
    this.scheduleAddTaskToBasket = scheduleAddTaskToBasket;
    this.addPermanentTask = addPermanentTask;
    this.handlePermanentTaskRemoval = handlePermanentTaskRemoval;
    this.getState = getState;
    this.setState = setState;
  }

  /**
   * Determines if a task should be scheduled based on its preselect type
   */
  public shouldScheduleTask(
    task: ITaskItem,
    isWeekendDay: boolean
  ): { addTask: boolean; scheduleReason: string } {
    if (task.preSelect === PreselectType.daily) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          task.name,
          "Daily Enforced Task Applied",
          TraceLevel.info
        )
      );
      return {
        addTask: true,
        scheduleReason:
          "This task is enforced to be run daily by the settings of the task. It is not excluded, and matches the members diary and within their limits",
      };
    } else if (!isWeekendDay && task.preSelect === PreselectType.weekdays) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          task.name,
          "Weekday Enforced Task Applied",
          TraceLevel.info
        )
      );
      return {
        addTask: true,
        scheduleReason:
          "This task is enforced to be run on week days by the settings of the task. Today is a weekday according to the members diary. It is not excluded and within their limits",
      };
    } else if (isWeekendDay && task.preSelect === PreselectType.weekends) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          task.name,
          "Weekend Enforced Task Applied",
          TraceLevel.info
        )
      );
      return {
        addTask: true,
        scheduleReason:
          "This task is enforced to be run on weekends by the settings of the task. Today is a weekend according to the members diary. It is not excluded and within their limits",
      };
    }
    return { addTask: false, scheduleReason: "" };
  }

  /**
   * Sorts tasks with FreeDays availability to the top
   */
  public sortFreeDayToTop(x: ITaskItem, y: ITaskItem): number {
    if (
      x.availabilityDayType === AvailabilityDayType.FreeDays &&
      y.availabilityDayType !== AvailabilityDayType.FreeDays
    )
      return -1;
    if (
      x.availabilityDayType !== AvailabilityDayType.FreeDays &&
      y.availabilityDayType === AvailabilityDayType.FreeDays
    )
      return 1;
    return x.name?.localeCompare(y.name);
  }

  /**
   * Checks if a task can be added to the schedule (not conflicting with existing tasks)
   */
  public canScheduleTask(
    task: ITaskItem,
    taskSchedule: ITaskBasketInfo,
    scheduler: ISchedulerTimerInfo[]
  ): boolean {
    if (taskSchedule[task.key]) return false; // already set up
    if (scheduler.findIndex((sv) => sv.key === task.key) > -1) return false; // it is already scheduled for today
    if (
      !this.settings.canAddCategory(
        task,
        Object.values(this.settings.getTaskSchedule())
      )
    ) {
      return false; // means we are conflicted on this item another with same category has been added
    }
    return true;
  }

  /**
   * Handles enforced task scheduling
   * @param intraDay do we only check for changes in permanent tasks or sweep through all tasks that could be applied today
   * @param customDay is this for a custom day schedule
   */
  public handleEnforcedTasks(intraDay: boolean, customDay?: boolean): Promise<void> {
    return new Promise<void>((resolve) => {
      const currentState = this.getState();
      if (currentState === "uninitialised" || currentState === "addingEnforcedTasks")
        return resolve();
      if (this.settings.pendingRoll()) return resolve(); // if minute tick fires whilst we are awaiting a new day event
      if (this.settings.checkHolidayToday()) return resolve();

      const scheduler = this.settings.getScheduleInfo();
      this.setState("addingEnforcedTasks");

      const tasks = this.settings
        .getAvailableRandomTasks(intraDay, new Date())
        .filter((c) => TaskItem.IsScheduled(c))
        .sort((x, y) => this.sortFreeDayToTop(x, y));
      const isWeekendDay = this.settings.getIsWeekend();
      const canPerformSomeToday = this.settings.canPerformSomeTasksForToday();

      for (let idx = 0; idx < tasks.length; idx += 1) {
        // need to fetch a new each time here as the calls below can update the schedule beneath us
        const taskSchedule = this.settings.getTaskSchedule();
        const task = tasks[idx];

        if (TaskItem.IsPermanentlyApplied(task)) {
          // add it in without timer and set it as a static item
          const copyTask = new TaskItem(task);
          copyTask.credit = 0; //these don't pay out
          this.addPermanentTask(copyTask, taskSchedule);
        } else {
          // Daily/weekday/weekend enforced tasks always apply, including on custom schedule days.
          // Custom schedule events are handled separately; mandatory task rules stack on top.
          if (
            taskSchedule[task.key] &&
            TaskItem.IsPermanentlyApplied(
              taskSchedule[task.key] as ITaskTimerInfo
            )
          ) {
            this.handlePermanentTaskRemoval(task, taskSchedule);
          }
          if (intraDay) continue; // we have dealt with permanent task checks and now we can spin out
          if (!canPerformSomeToday) continue; // we are not free to perform tasks
          if (!this.canScheduleTask(task, taskSchedule, scheduler)) continue;

          const { addTask, scheduleReason } = this.shouldScheduleTask(task, isWeekendDay);
          if (addTask) {
            this.scheduleAddTaskToBasket(task, scheduleReason);
          }
        }
      }
      this.setState("ready");
      resolve();
    });
  }

  /**
   * Adds required random tasks at start of day based on daily mandatory credit settings
   */
  public onSODAddRequiredRandomTasks(customDay = false): void {
    if (customDay) return;

    const generalSettings = this.settings.getGeneralSettings();
    if (!this.settings.canPerformSomeTasksForToday()) return;

    const creditSettings = this.settings.getTaskSettings();
    if (
      creditSettings.disableSystemTasks &&
      !this.settings.getControl().isOwnedBySelf()
    )
      return;

    // add items by default
    let count = creditSettings.dailyMandatoryCredits;
    const isWeekendDay = this.settings.getIsWeekend();
    if (isWeekendDay) {
      if (generalSettings.weekendHandling === WeekendHandling.harsh)
        count += count;
      if (generalSettings.weekendHandling === WeekendHandling.easy)
        count = Math.round(count / 2);
    }

    const availableTasks = this.settings
      .getAvailableRandomTasks(false, new Date())
      .filter((i) => !TaskItem.IsScheduled(i));
    const maxToAdd =
      availableTasks.length > count ? count : availableTasks.length;
    if (maxToAdd !== count) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Alert,
          "Limit Reached",
          `Only able to select ${maxToAdd}/${count} Mandatory Items`,
          TraceLevel.warning
        )
      );
    }

    for (let i = 0; i < maxToAdd; i++) {
      const task = this.settings.getRandomTask(availableTasks);
      if (!task) continue;
      this.scheduleAddTaskToBasket(
        task,
        "This task was added by random selection for today based on the task settings daily mandatory tasks. The tasks selected are all matching the kinks and limits, available based on the diary of the member and the tasks are enabled and valid."
      );
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          task.name,
          "Mandatory Item Selected",
          TraceLevel.info
        )
      );
    }
  }
}
